import L from 'leaflet';

/**
 * lab-kaart.js — wat de kaart bij de labopdracht en de kaart in een apart venster delen.
 *
 * De kaart kan op een tweede scherm (idee van RSK): dan opent `/kaartvenster` in een eigen
 * venster en praten de twee via een BroadcastChannel. De pagina met de labopdracht blijft de
 * baas: die rekent uit welke boringen er staan, welke geselecteerd zijn en hoe ze eruitzien,
 * en stuurt dat als 'stand'. Het venster tekent alleen, en meldt terug wat je erop aanklikt.
 *
 * Berichten van de pagina naar het venster:
 *   { type: 'stand', punten, sublocations, highlightId, knoppen, gekozen, zonderPlek, kanTekenen, volgendeNaam, filter, pasIn }
 *       filter: het model van het filterblok (utils/lab-filterblok.js)
 *       punten: [{ id, naam, lat, lng, klasse, aan }] — klasse is de class van de stip (mm-punt ...)
 *       knoppen: [{ id, name, n }] — de deellocatieknoppen; gekozen / zonderPlek: boringnamen
 *   { type: 'deellocatie-klaar' | 'deellocatie-fout', verzoek, message? }  uitkomst van opslaan
 *   { type: 'markeer', id }   de boring die je in de profielen aanwijst (of null)
 *   { type: 'sluit' }         de pagina gaat weg of haalt de kaart terug
 * Van het venster naar de pagina:
 *   { type: 'hallo' }                     net geopend: stuur de stand
 *   { type: 'wissel', id }                boring aangeklikt
 *   { type: 'kader', ids, erbij }         kader getrokken (erbij: ctrl/cmd)
 *   { type: 'deellocatie', id, erbij }    deellocatie aangeklikt (op de kaart of met een knop)
 *   { type: 'wis-selectie' }
 *   { type: 'filter', actie, waarde }     het filter aangepast in het venster: actie is 'patch',
 *                                         'boringFilter', 'wisselBzb' of 'wisFilters'
 *   { type: 'nieuwe-deellocatie', name, ring, verzoek }   getekend vlak; de pagina slaat het op
 *   { type: 'deellocatie-hernoem', id, name, verzoek } / { type: 'deellocatie-verwijder', id, verzoek }
 *       antwoord: { type: 'beheer-klaar' | 'beheer-fout', verzoek, message? }
 *   { type: 'dicht' }                     venster gesloten
 */

/**
 * Dezelfde basiskaart als het tabblad Kaart: MapTiler wereldwijd met PDOK-BRT erover voor
 * scherp Nederlands detail. Eén kaartbeeld in de hele applicatie.
 */
export const MAPTILER_KEY = import.meta.env.VITE_MAPTILER_KEY || '';
export const TILE_WORLD = `https://api.maptiler.com/maps/streets-v2/256/{z}/{x}/{y}.png?key=${MAPTILER_KEY}`;
export const TILE_BRT = 'https://service.pdok.nl/brt/achtergrondkaart/wmts/v2_0/standaard/EPSG:3857/{z}/{x}/{y}.png';
export const MT_ATTR = '&copy; <a href="https://www.maptiler.com/" target="_blank" rel="noopener">MapTiler</a> &copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a> contributors';
export const PDOK_ATTR = '&copy; <a href="https://www.pdok.nl" target="_blank" rel="noopener">PDOK</a> / Kadaster';

/** Een sleep korter dan dit telt als klik, niet als kader. */
export const KLIK_DREMPEL_PX = 4;

/** Naam van het kanaal tussen een pagina en zijn kaartvenster. */
export const kanaalNaam = (sleutel) => `mv-lab-kaart-${sleutel}`;

/** De stippen van de boringen; in de static styles van beide kaarten. */
export const MARKER_CSS = `
  .mm-punt { display:block; width:9px; height:9px; border-radius:50%; background:#fff;
             border:2.5px solid var(--mv-primary); transform:translate(-50%,-50%); cursor:pointer;
             transition:width .12s ease, height .12s ease, box-shadow .12s ease, background .12s ease, opacity .12s ease; }
  .mm-punt.levert { width:12px; height:12px; background:#a9cf9c; }
  .mm-punt.aan { width:15px; height:15px; background:var(--mv-primary); border-color:#2f6122;
                 box-shadow:0 0 0 5px rgba(75,157,53,.18); }
  .mm-punt.gedimd { opacity:.25; }
  .mm-punt.hover { width:21px; height:21px; background:#22371f; border-color:#fff; opacity:1;
                   box-shadow:0 0 0 6px rgba(75,157,53,.45), 0 2px 10px rgba(16,28,18,.45); }
  .kaart.markeert .mm-punt:not(.hover) { opacity:.35; }
  .kaart.markeert .mm-punt:not(.hover) + .mm-naam { opacity:.35; }
  .mm-punt.hover + .mm-naam { background:#22371f; color:#fff; }
  .mm-naam { position:absolute; left:9px; top:0; transform:translateY(-50%); padding:0 4px; border-radius:4px;
             background:#fff; border:1px solid #cddccb; color:#2f6122; font:900 .62rem/1.5 var(--mv-font); white-space:nowrap; }
`;

/** De html van een stip (met naamlabel als hij geselecteerd is), voor een L.divIcon. */
export function markerHtml({ klasse, naam, aan }) {
  const veilig = String(naam || '?').replace(/[<>&"]/g, '');
  return `<span class="${klasse}" title="${veilig}"></span>` + (aan ? `<span class="mm-naam">${veilig}</span>` : '');
}

/**
 * Telt deze klik of dit kader bij de selectie op? Ctrl, ⌘ of shift.
 *
 * Shift erbij omdat ctrl op een Mac een rechtermuisklik is: dan opent het contextmenu (zie
 * koppelKader). Ctrl werkt daar nu ook, maar shift is wat Mac-gebruikers verwachten.
 */
export const erbijToets = (e) => !!(e?.ctrlKey || e?.metaKey || e?.shiftKey);

/** De uitleg onder de kaart, op beide schermen dezelfde. */
export const KADER_UITLEG = 'sleep een kader om boringen te selecteren, of klik ze los aan · met shift (of ctrl) telt een kader bij je selectie op';

/**
 * Een kader trekken over de kaart om boringen te selecteren. Gedeeld door de kaart bij de
 * labopdracht en het kaartvenster.
 *
 * Een sleep korter dan KLIK_DREMPEL_PX is een klik: die laat de selectie met rust.
 *
 * Twee valkuilen die hier worden afgevangen (gemeld 29-10-2026, MacBook):
 *  - Ctrl + slepen is op een Mac een rechtermuisklik. Het contextmenu ving dan het loslaten van
 *    de muis op, de kaart hoorde nooit dat het slepen klaar was, en het kader bleef staan.
 *    Tijdens het selecteren onderdrukken we dat menu.
 *  - Een kader dat toch blijft hangen (muis losgelaten buiten de kaart, venster kwijt) ruimen we
 *    op bij de volgende klik en bij elk loslaten buiten de kaart.
 *
 * @param {L.Map} kaart
 * @param {{ magSelecteren: () => boolean, opDruk?: (e) => void, bijKader: (grens: L.LatLngBounds, erbij: boolean) => void }} o
 * @returns {() => void} ontkoppelen
 */
export function koppelKader(kaart, { magSelecteren, opDruk = () => {}, bijKader }) {
  let start = null, startPunt = null, rechthoek = null;
  const stop = () => { rechthoek?.remove(); start = null; startPunt = null; rechthoek = null; };

  kaart.on('mousedown', (e) => {
    opDruk(e);
    stop();   // een achtergebleven kader van een vorige keer
    // Alleen de linkerknop (op een Mac ook ctrl + klik, dat meldt zich als knop 0).
    if (!magSelecteren() || (e.originalEvent && e.originalEvent.button !== 0)) return;
    start = e.latlng;
    startPunt = e.containerPoint;
    rechthoek = L.rectangle(L.latLngBounds(start, start), {
      color: '#4b9d35', weight: 1.5, dashArray: '4 3', fillOpacity: 0.12, interactive: false,
    }).addTo(kaart);
  });
  kaart.on('mousemove', (e) => { if (start) rechthoek.setBounds(L.latLngBounds(start, e.latlng)); });
  kaart.on('mouseup', (e) => {
    if (!start) return;
    const grens = L.latLngBounds(start, e.latlng);
    const gesleept = startPunt.distanceTo(e.containerPoint) >= KLIK_DREMPEL_PX;
    const erbij = erbijToets(e.originalEvent);
    stop();
    if (gesleept) bijKader(grens, erbij);
  });
  // De muis die de kaart verlaat terwijl je sleept, laat anders een kader achter.
  kaart.on('mouseout', (e) => { if (start && !e.originalEvent?.relatedTarget) stop(); });

  const container = kaart.getContainer();
  const geenMenu = (e) => { if (magSelecteren()) e.preventDefault(); };
  // Via composedPath en niet e.target: de kaart zit in een shadow DOM, en op window is het doel
  // dan het component zelf. Dan leek elk loslaten 'buiten de kaart', en verdween het kader
  // vóórdat de kaart het kon afhandelen -- selecteren deed niets meer (29-10-2026).
  const losErgens = (e) => { if (start && !e.composedPath().includes(container)) stop(); };
  container.addEventListener('contextmenu', geenMenu);
  window.addEventListener('mouseup', losErgens, true);
  return () => {
    stop();
    container.removeEventListener('contextmenu', geenMenu);
    window.removeEventListener('mouseup', losErgens, true);
  };
}
